import { Form, Select } from 'antd';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { getFormItemLabel } from '@/components/form-fields/utils';
import { SEARCH_DEBOUNCE_MS, useDebouncedCallback } from '@/utils/debounce';
import { api } from '@/api';

const { Option } = Select;
const SEARCH_PAGE_SIZE = 30;

interface OptionItem {
  value: number;
  label: string;
}

export const CampaignSearchSelect = observer(
  ({
    name,
    rules,
    i18nLabel,
    readOnly,
    allowClear = true,
    initialSelectedOptions = [],
    ...rest
  }: {
    name: string;
    rules?: any[];
    i18nLabel: string;
    readOnly?: boolean;
    allowClear?: boolean;
    initialSelectedOptions?: OptionItem[];
  }) => {
    const [options, setOptions] = useState<OptionItem[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
      fetchCampaigns('');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchCampaigns = useCallback(
      (searchText: string) => {
        if ((initialSelectedOptions?.length ?? 0) === 0) setLoading(true);
        api.campaigns
          .getAll({
            name: searchText || undefined,
            limit: SEARCH_PAGE_SIZE,
            skip: 0,
          })
          .source.then(({ data }: { data: { items: cyrian.api.campaigns.Campaign[] } }) => {
            setOptions(
              (data?.items ?? []).map((c) => ({
                value: c.id,
                label: c.name?.trim() || c.year || String(c.id),
              })),
            );
          })
          .catch(() => setOptions([]))
          .finally(() => setLoading(false));
      },
      [initialSelectedOptions?.length],
    );

    const debouncedFetch = useDebouncedCallback(fetchCampaigns, SEARCH_DEBOUNCE_MS);

    const onSearch = useCallback(
      (value: string) => {
        if (value === undefined || value === null) {
          setOptions([]);
          return;
        }
        const trimmed = String(value).trim();
        debouncedFetch(trimmed);
      },
      [debouncedFetch],
    );

    const mergedOptions = useMemo(() => {
      const byId = new Map<number, OptionItem>();
      (initialSelectedOptions ?? []).forEach((o) => {
        byId.set(o.value, o);
      });
      options.forEach((o) => {
        byId.set(o.value, o);
      });
      return Array.from(byId.values());
    }, [initialSelectedOptions, options]);

    const label = getFormItemLabel(i18nLabel, undefined);

    return (
      <Form.Item name={name} label={label} rules={rules}>
        <Select
          showSearch
          allowClear={allowClear}
          placeholder={label}
          filterOption={false}
          onSearch={onSearch}
          onFocus={() => options.length === 0 && fetchCampaigns('')}
          loading={loading}
          disabled={readOnly}
          {...rest}
        >
          {mergedOptions.map((opt) => (
            <Option key={opt.value} value={opt.value}>
              {opt.label}
            </Option>
          ))}
        </Select>
      </Form.Item>
    );
  },
);
